"use client";

import { useEffect, useState } from "react";
import { fetchQuestions, updateQuestion } from "../../../services/AdminService";
import { getClientId } from "../../../services/Service";

export default function UpdateOptions() {
  const [clientId, setClientId] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getQuestionTypeFromLink = (link) => {
    const videoExtensions = [".mp4", ".avi", ".mov"];
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];
    const fileExtensions = [".pdf", ".docx"];
    const domainExtensions = [".com", ".in", ".org", ".net", ".html", ".js"];
    const lowerCaseLink = (link || "").toLowerCase();

    if (!lowerCaseLink) return 6;
    if (videoExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 4;
    if (imageExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 3;
    if (fileExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 2;
    if (domainExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 5;
    return 6;
  };

  const extractQuestionsFromResponse = (response) => {
    const collected = [];
    const seenIds = new Set();

    const pushIfQuestion = (item) => {
      if (!item || typeof item !== "object") return;
      if (
        Object.prototype.hasOwnProperty.call(item, "id") &&
        (Object.prototype.hasOwnProperty.call(item, "question_text") ||
          Object.prototype.hasOwnProperty.call(item, "question_level") ||
          Object.prototype.hasOwnProperty.call(item, "question_parent_level"))
      ) {
        const id = item.id;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          collected.push(item);
        }
      }
    };

    const walk = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v && typeof v === "object") {
            pushIfQuestion(v);
            Object.values(v).forEach((inner) => walk(inner));
          }
        });
        return;
      }
      if (typeof value === "object") {
        pushIfQuestion(value);
        Object.values(value).forEach((v) => walk(v));
      }
    };

    walk(response);
    return collected;
  };

  const initEditableRows = (questions) => {
    const sorted = [...questions].sort(
      (a, b) => (a.question_level || 0) - (b.question_level || 0)
    );

    return sorted.map((q) => {
      const isLinkType = [2, 3, 4, 5, 6].includes(Number(q.question_type));
      return {
        id: q.id,
        level: q.question_level,
        parentLevel: q.question_parent_level,
        text: q.question_text || "",
        mode: isLinkType ? "link" : "text",
        label: isLinkType ? q.question_text || "" : q.question_label || "",
        url: isLinkType ? q.question_label || "" : "",
      };
    });
  };

  useEffect(() => {
    const id = getClientId() || sessionStorage.getItem("clientId");
    setClientId(id);

    if (!id) {
      setLoading(false);
      setError("Client ID is missing.");
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        const data = await fetchQuestions(id);
        const list = extractQuestionsFromResponse(data);
        setRows(initEditableRows(list));
      } catch (err) {
        console.error("Error loading options:", err);
        setError(err?.message || "Failed to load options.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleTextChange = (id, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, text: value } : r)));
  };

  const handleLabelChange = (id, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, label: value } : r)));
  };

  const handleUrlChange = (id, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, url: value } : r)));
  };

  const handleModeChange = (id, mode) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              mode,
              label: mode === "link" ? r.text : r.label,
            }
          : r
      )
    );
  };

  const handleReset = () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    setSuccess("");

    (async () => {
      try {
        const data = await fetchQuestions(clientId);
        const list = extractQuestionsFromResponse(data);
        setRows(initEditableRows(list));
      } catch (err) {
        console.error("Error reloading options:", err);
        setError(err?.message || "Failed to reload options.");
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleSave = async () => {
    if (!clientId) return;

    setError("");
    setSuccess("");

    for (const row of rows) {
      if (!row.text.trim()) {
        setError(`Message text is required for level ${row.level}.`);
        return;
      }
      if (row.mode === "link" && !row.url.trim()) {
        setError(`URL is required for link-type option at level ${row.level}.`);
        return;
      }
    }

    try {
      setSaving(true);

      const numericClientId = Number(clientId) || 1;
      const questionsPayload = rows.map((row) => {
        let p_question_type = 1;
        let p_question_label = null;

        if (row.mode === "link") {
          p_question_label = row.url.trim();
          p_question_type = getQuestionTypeFromLink(row.url);
        } else {
          p_question_label = row.label?.trim() || null;
          p_question_type = 1;
        }

        return {
          action_type: "U",
          p_id: row.id,
          p_question_text: row.text.trim(),
          p_question_label,
          p_question_type,
          p_client_id: numericClientId,
          p_question_level: row.level,
          p_question_parent_level: row.parentLevel === undefined ? null : row.parentLevel,
        };
      });

      await updateQuestion({ questions: questionsPayload });
      setSuccess("Options updated successfully.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating options:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to update options.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5">
        <div className="spinner-border me-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span>Loading options...</span>
      </div>
    );
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const displayedRows = rows.filter((row) => {
    if (!normalizedSearch) return true;
    const searchable = [
      String(row.level ?? ""),
      String(row.id ?? ""),
      row.text,
      row.label,
      row.url,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchable.includes(normalizedSearch);
  });

  return (
    <div className="container-fluid mt-3 update-options-page">
      <div className="card border-0 shadow-sm update-options-hero mb-3">
        <div className="card-body d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <div className="update-options-kicker">Conversation Builder</div>
            <h3 className="mb-1">Update Bot Options</h3>
            <p className="mb-0 text-muted">Edit option text and link metadata for each configured level.</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge text-bg-light border">Total Options: {rows.length}</span>
            {/* <span className="badge text-bg-primary">Visible: {displayedRows.length}</span> */}
          </div>
        </div>
      </div>

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess("")} />
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError("")} />
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-muted">No options found for this client.</p>
      ) : (
        <>
          <div className="card border-0 shadow-sm update-options-card">
            <div className="card-body pb-2">
              <div className="row g-2 align-items-end mb-3">
                <div className="col-md-6 col-lg-5">
                  <label className="form-label fw-semibold mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control update-options-input"
                    placeholder="Search by level, message, label or URL"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-6 col-lg-7">
                  <div className="small text-muted">
                    Use <strong>Text</strong> for normal options and <strong>Link</strong> when URL is required.
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0 update-options-table">
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>Level</th>
                    <th style={{ width: "42%" }}>Leadbot Options</th>
                    <th>Type &amp; Link details</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-4">
                        No matching options found.
                      </td>
                    </tr>
                  ) : (
                    displayedRows.map((row) => (
                      <tr key={row.id}>
                        <td className="update-options-level-cell text-center">
                          <span className="update-options-level-pill">{row.level ?? "-"}</span>
                        </td>
                        <td className="update-options-message-cell">
                          <input
                            type="text"
                            className="form-control form-control-sm update-options-input-sm"
                            value={row.text}
                            onChange={(e) => handleTextChange(row.id, e.target.value)}
                            placeholder="Main message shown to visitor"
                          />
                        </td>
                        <td className="update-options-type-cell">
                          <div className="mb-2">
                            <div className="btn-group btn-group-sm update-options-mode-group" role="group">
                              <button
                                type="button"
                                className={`btn ${row.mode === "text" ? "btn-dark" : "btn-outline-secondary"}`}
                                onClick={() => handleModeChange(row.id, "text")}
                              >
                                Text
                              </button>
                              <button
                                type="button"
                                className={`btn ${row.mode === "link" ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => handleModeChange(row.id, "link")}
                              >
                                Link
                              </button>
                            </div>
                          </div>

                          {row.mode === "text" && (
                            <input
                              type="text"
                              className="form-control form-control-sm update-options-input-sm"
                              value={row.label}
                              onChange={(e) => handleLabelChange(row.id, e.target.value)}
                              placeholder="Optional extra label"
                            />
                          )}

                          {row.mode === "link" && (
                            <div className="d-flex flex-column gap-1">
                              <input
                                type="text"
                                className="form-control form-control-sm update-options-input-sm"
                                value={row.label}
                                onChange={(e) => handleLabelChange(row.id, e.target.value)}
                                placeholder="Display label"
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm update-options-input-sm"
                                value={row.url}
                                onChange={(e) => handleUrlChange(row.id, e.target.value)}
                                placeholder="Link URL"
                              />
                              <small className="text-muted">Type is auto-detected from URL (file/image/video/redirect).</small>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 d-flex justify-content-center gap-2">
            <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={handleReset} disabled={saving}>
              Reset
            </button>
            <button type="button" className="btn btn-success rounded-pill px-3" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </>
      )}

      <style>{`
        .update-options-page {
          --u-bg: #f4f8ff;
          --u-border: #dbe4f0;
          background:
            radial-gradient(circle at 4% 0%, rgba(14, 165, 233, 0.14), transparent 28%),
            radial-gradient(circle at 94% 22%, rgba(37, 99, 235, 0.1), transparent 32%),
            var(--u-bg);
          border-radius: 18px;
          padding-bottom: 8px;
        }
        .update-options-hero {
          border: 1px solid var(--u-border);
          background: linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
        }
        .update-options-kicker {
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #1d4ed8;
          margin-bottom: 6px;
        }
        .update-options-card {
          border: 1px solid var(--u-border);
          background: #ffffff;
        }
        .update-options-table thead th {
          background: linear-gradient(90deg, #0f172a 0%, #1e293b 100%);
          color: #f8fafc;
          border-bottom-width: 0;
          font-weight: 700;
          letter-spacing: 0.01em;
          vertical-align: middle;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .update-options-table tbody td {
          vertical-align: middle;
          padding: 0.75rem;
        }
        .update-options-table tbody tr:hover {
          background: #f4f8ff;
        }
        .update-options-level-cell {
          min-width: 90px;
        }
        .update-options-level-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
          height: 34px;
          border-radius: 999px;
          border: 1px solid #bfdbfe;
          background: #e0ecff;
          color: #1e3a8a;
          font-weight: 800;
        }
        .update-options-message-cell,
        .update-options-type-cell {
          min-width: 260px;
        }
        .update-options-mode-group .btn {
          min-width: 72px;
        }
        .update-options-input {
          min-height: 44px;
          border-radius: 12px;
          border-color: #d4deea;
        }
        .update-options-input-sm {
          min-height: 34px;
          border-radius: 10px;
          border-color: #d4deea;
        }
        .update-options-input:focus,
        .update-options-input-sm:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
        }
        @media (max-width: 991.98px) {
          .update-options-table thead th {
            position: static;
          }
          .update-options-table tbody td {
            padding: 0.6rem;
          }
          .update-options-mode-group .btn {
            min-width: 64px;
          }
        }
      `}</style>
    </div>
  );
}
